import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { visibleSections, isActivePath } from '@/lib/navConfig';
import { NavBadge } from './NavBadge';

export const MobileMenu = ({ user, badges, languages, currentLang, onLanguageChange, onAction, onLogout, onClose }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const linkCls = (active) =>
    `px-3 py-2 rounded-md flex items-center justify-between gap-2 ${active ? 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 font-medium' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`;

  return (
    <div className="md:hidden border-t bg-background p-4 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
      <nav className="flex flex-col gap-1">
        <Link to="/" className={linkCls(pathname === '/')} onClick={onClose}>{t('nav.home')}</Link>
        {user && visibleSections(user).map((section) => {
          const SIcon = section.icon;
          return (
            <div key={section.key}>
              <div className="mt-3 mb-1 px-3 pt-2 border-t">
                <span className={`text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 ${section.color}`}>
                  <SIcon className="h-3 w-3" /> {t(section.labelKey)}
                </span>
              </div>
              {section.items.map((item) => {
                const Icon = item.icon;
                const count = item.badge ? badges?.[item.badge] || 0 : 0;
                const testId = `mobile-${item.testId || `nav-${item.key}`}`;
                if (item.action) {
                  return (
                    <button key={item.key} type="button" className={`${linkCls(false)} w-full text-left`} onClick={() => { onAction(item.action); onClose(); }} data-testid={testId}>
                      <span className="flex items-center gap-2"><Icon className={`h-4 w-4 ${item.iconColor || ''}`} /> {t(item.labelKey)}</span>
                    </button>
                  );
                }
                return (
                  <Link key={item.key} to={item.to} className={linkCls(isActivePath(pathname, item.to))} onClick={onClose} data-testid={testId}>
                    <span className="flex items-center gap-2"><Icon className={`h-4 w-4 ${item.iconColor || ''}`} /> {t(item.labelKey)}</span>
                    <NavBadge count={count} tone={item.badge === 'messages' ? 'blue' : 'red'} testId={`${testId}-badge`} />
                  </Link>
                );
              })}
            </div>
          );
        })}
        {user && (
          <button type="button" className="mt-3 px-3 py-2 rounded-md flex items-center gap-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 text-left" onClick={() => { onLogout(); onClose(); }} data-testid="mobile-logout-button">
            <LogOut className="h-4 w-4" /> {t('nav.logout')}
          </button>
        )}
      </nav>
      <div className="mt-4 pt-4 border-t">
        <p className="text-sm text-muted-foreground mb-2">{t('nav.language')}</p>
        <div className="flex flex-wrap gap-2">
          {languages.map((lang) => (
            <Button key={lang.code} variant={currentLang === lang.code ? 'default' : 'outline'} size="sm"
              onClick={() => onLanguageChange(lang.code)} className={currentLang === lang.code ? 'bg-orange-500' : ''} data-testid={`mobile-lang-${lang.code}`}>
              {lang.flag}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
